import { Component, Element, Prop, h, Host, Listen, State, Watch, forceUpdate } from '@stencil/core';
import { arrow_left, arrow_right } from 'pn-design-assets/pn-assets/icons.js';

/**
 * The `pn-segmented-control` and `pn-segmented` is built with native `input[type="radio"]` in the background.
 * This means you can use native events to listen to the changes.
 *
 * @nativeChange Use the `change` event to listen when the segment state is being changed.
 */
@Component({
  tag: 'pn-segmented-control',
  styleUrl: 'pn-segmented-control.scss',
})
export class PnSegmentedControl {
  mo: MutationObserver;

  segmentContainer: HTMLDivElement;
  segments: HTMLPnSegmentElement[] = [];
  activeBg: HTMLDivElement;
  hoverBg: HTMLDivElement;

  scrollRegistered: boolean = false;

  @Element() hostElement: HTMLElement;

  @State() showScrollArrows: boolean = false;
  @State() showLeftArrow: boolean = false;
  @State() showRightArrow: boolean = false;

  /** This is the name of the radio buttons inside the controller. @deprecated Set the name in the nested `pn-segment` components. */
  @Prop() name!: string;
  /** Currently active segment value. */
  @Prop({ mutable: true }) value: string;

  @Watch('value')
  valueHandler() {
    this.setActiveSegment();
  }

  @Listen('change')
  changeHandler(event: InputEvent) {
    const target = event.target as HTMLInputElement;

    this.value = target.value;
    this.setActiveSegment();
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.rerender();
  }

  @Listen('segmentHover')
  handleHover(event: CustomEvent) {
    const { target } = event.detail;
    this.calcHighlight(target, true);
  }

  componentDidLoad() {
    if (this.mo) this.mo.disconnect();

    this.mo = new MutationObserver(() => {
      forceUpdate(this.hostElement);
      this.setSegments();
    });

    this.mo.observe(this.hostElement, { childList: true });

    this.setSegments();
  }

  setSegments() {
    this.segments = Array.from(this.hostElement.querySelectorAll('pn-segment'));
    this.setActiveSegment();
  }

  setActiveSegment() {
    this.segments.forEach(segmentEl => {
      if (this.value === segmentEl.value) segmentEl.setAttribute('selected', 'true');
      else segmentEl.removeAttribute('selected');
    });
    this.rerender();
  }

  rerender() {
    requestAnimationFrame(() => {
      this.calcHighlight();
      this.scrollArrowRender();
    });
  }

  /*---------------------------------------HIGHLIGHT LOGIC-------------------------------------------*/
  calcHighlight(element?: HTMLElement, hover?: boolean) {
    const chip = hover ? this.hoverBg : this.activeBg;
    requestAnimationFrame(() => {
      const el = element || this.segments.find(({ value }) => value === this.value);
      const elSegment = el?.closest('pn-segment');
      if (!elSegment) return;
      const elRect = elSegment.getBoundingClientRect();
      const { left: hostLeft } = this.segmentContainer.getBoundingClientRect();
      const { left: segmentLeft, height: segmentHeight, width: segmentWidth } = elRect;

      const offset = segmentLeft - hostLeft + this.segmentContainer.scrollLeft;

      chip.style.setProperty('transform', `translate(${offset}px, -50%`);
      chip.style.setProperty('width', `${segmentWidth}px`);
      chip.style.setProperty('height', `${segmentHeight}px`);
      (el as HTMLPnSegmentElement).disabled ? (chip.dataset.disabled = '') : delete chip.dataset.disabled;
    });
  }
  /*---------------------------------------/HIGHLIGHT LOGIC-------------------------------------------*/

  /*---------------------------------------SCROLL ARROW LOGIC-------------------------------------------*/
  scrollArrowRender() {
    if (this.segmentContainer.scrollWidth > this.segmentContainer.clientWidth) {
      this.showScrollArrows = true;

      if (!this.scrollRegistered) {
        this.segmentContainer.addEventListener('scroll', this.scrollArrowRender.bind(this));
        this.scrollRegistered = true;
      }

      const amountScrolled = Math.round(this.segmentContainer.scrollWidth - this.segmentContainer.scrollLeft);
      const distanceToEnd = amountScrolled - this.segmentContainer.clientWidth;
      const distanceToStart = this.segmentContainer.scrollLeft;

      this.showLeftArrow = distanceToStart > 0;
      this.showRightArrow = distanceToEnd > 0;
      return;
    }

    this.showLeftArrow = false;
    this.showRightArrow = false;
    this.showScrollArrows = false;
  }

  scroll(val: number) {
    let amount = this.segmentContainer.scrollLeft + val;

    this.segmentContainer.scroll({
      left: amount,
      behavior: 'smooth',
    });
  }

  scrollArrowClasses() {
    const list = ['pn-sc-arrows'];

    if (this.showLeftArrow) list.push('pn-sc-left-visible');
    if (this.showRightArrow) list.push('pn-sc-right-visible');

    return list.join(' ');
  }
  /*---------------------------------------/SCROLL ARROW LOGIC-------------------------------------------*/

  render() {
    return (
      <Host>
        <div class="pn-segmented-control" ref={el => (this.segmentContainer = el)}>
          <slot />
          <div class="pn-sc-bg" data-active ref={el => (this.activeBg = el)}></div>
          <div class="pn-sc-bg" data-hover ref={el => (this.hoverBg = el)}></div>
        </div>

        <div class={this.scrollArrowClasses()} hidden={!this.showScrollArrows}>
          <button
            aria-label="Left"
            class="pn-sc-arrow pn-sc-arrow-left"
            onClick={() => this.scroll(-120)}
            tabindex="-1"
          >
            <pn-icon icon={arrow_left} />
          </button>

          <button
            aria-label="Right"
            class="pn-sc-arrow pn-sc-arrow-right"
            onClick={() => this.scroll(120)}
            tabindex="-1"
          >
            <pn-icon icon={arrow_right} />
          </button>
        </div>
      </Host>
    );
  }
}
